import { useMemo, useState } from "react";
import {
  Box,
  Dialog,
  Grid,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
} from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import SearchIcon from "@mui/icons-material/Search";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ByzMuiDataGrid from "src/components/core-components/mui/data-grid";
import { DietRequest, Patient } from "./doctor-data-grid";
import { useTranslate } from "src/locales";
import ByzDataGridToolbar from "src/components/core-components/mui/data-grid/toolbar";

//CKAPISIZ;452953;30.04.2025

interface DoctorDietListProps {
  open: boolean;
  onClose: () => void;
  data: DietRequest[];
  patient: Patient | null;
}

export default function DoctorDietList({
  open,
  onClose,
  data,
  patient,
}: DoctorDietListProps) {
  const [activeTab, setActiveTab] = useState(0);
  const [inputSearchValue, setInputSearchValue] = useState("");

  const { t } = useTranslate();

  const uniqueDates = useMemo(
    () =>
      Array.from(new Set(data.map((d) => d.dietTime))).sort((a, b) =>
        a < b ? 1 : -1
      ),
    [data]
  );
  const rows = useMemo(() => {
    const filtered = data
      .filter((d) => d.dietTime === uniqueDates[activeTab])
      .filter((d) =>
        inputSearchValue
          ? Object.values(d).some((v) =>
              String(v).toLowerCase().includes(inputSearchValue.toLowerCase())
            )
          : true
      );

    const infoRow: DietRequest = {
      id: -1,
      patientId: patient?.id ?? 0,
      dietTime: uniqueDates[activeTab],
      approvalStatus: "",
      name: "",
      meal: "",
      dietType: "",
      note: "",
      companion: "",
      actionTime: "",
      actionBy: "",
      approvalOrCancel: "",
    };

    return [infoRow, ...filtered];
  }, [data, uniqueDates, activeTab, inputSearchValue, patient]);

  const requestCount = rows.length - 1;

  const columns: GridColDef[] = [
    {
      field: "dietTime",
      headerName: t("diet-requests-doctor.diet-time"),
      flex: 1,
      renderCell: (params: GridRenderCellParams) =>
        params.id === -1 ? uniqueDates[activeTab] : params.value,
    },
    {
      field: "approvalStatus",
      headerName: t("diet-requests-doctor.approval-status"),
      flex: 1,
      renderCell: (params: GridRenderCellParams) =>
        params.id === -1 ? `${requestCount} istek` : params.value,
      cellClassName: (params) => {
        const v = params.value as string;
        if (v === t("diet-requests-doctor.sent")) return "status-sent";
        if (v === t("diet-requests-doctor.approved")) return "status-approved";
        if (v === t("diet-requests-doctor.cancel")) return "status-rejected";
        if (v === t("diet-requests-doctor.pending")) return "status-pending";
        return "";
      },
    },
    {
      field: "name",
      headerName: t("diet-requests-doctor.name"),
      flex: 1,
      renderCell: (params) => (params.id === -1 ? null : params.value),
    },
    {
      field: "meal",
      headerName: t("meal"),
      flex: 1,
      renderCell: (params) => (params.id === -1 ? null : params.value),
    },
    {
      field: "dietType",
      headerName: t("diet-requests-doctor.diet-type"),
      flex: 1,
      renderCell: (params) => (params.id === -1 ? null : params.value),
    },
    {
      field: "note",
      headerName: t("note"),
      flex: 1,
      renderCell: (params) => (params.id === -1 ? null : params.value),
    },
    {
      field: "companion",
      headerName: t("diet-requests-doctor.companion"),
      flex: 1,
      renderCell: (params) => (params.id === -1 ? null : params.value),
    },
    {
      field: "actionTime",
      headerName: t("diet-requests-doctor.transaction-time"),
      flex: 1,
      renderCell: (params) => (params.id === -1 ? null : params.value),
    },
    {
      field: "actionBy",
      headerName: t("diet-requests-doctor.performed-by"),
      flex: 1,
      renderCell: (params) => (params.id === -1 ? null : params.value),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          minWidth: "70vw",
          minHeight: "70vh",
        },
      }}
    >
      <Grid container>
        <Grid md={2} sx={{ borderRight: "1px solid #E0E0E0" }}>
          <Box sx={{ p: 2 }}>
            <ByzMuiTypography isTitle>
              {t("diet-requests-doctor.diet-history")}
            </ByzMuiTypography>
            <ByzMuiTypography sx={{ color: "gray", mt: 1 }}>
              {" "}
              {t("diet-requests-doctor.full-name")}
            </ByzMuiTypography>
            <ByzMuiTypography>{patient?.nameSurname}</ByzMuiTypography>
            <ByzMuiTypography sx={{ color: "gray", mt: 2 }}>
              {t("diet-requests-doctor.file-or-protocol")}
            </ByzMuiTypography>
            <ByzMuiTypography>
              {patient?.fileNo} / {patient?.protocol}
            </ByzMuiTypography>
          </Box>
          <Box sx={{ py: 1, px: 2 }}>
            <ByzMuiTextField
              fullWidth
              size="small"
              placeholder="Ara..."
              value={inputSearchValue}
              onChange={(e) => setInputSearchValue(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Tabs
            orientation="vertical"
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            TabIndicatorProps={{ style: { display: "none" } }}
            sx={{
              borderTop: "1px solid #E0E0E0",
              "& .MuiTab-root": {
                width: "100%",
                justifyContent: "flex-start",
                textTransform: "none",
                borderBottom: "1px solid #E0E0E0 !important",
                py: 1,
                px: 2,
              },
              "& .MuiTab-root.Mui-selected": {
                color: (theme) => theme.palette.text.primary,
                fontWeight: "bold",
              },
            }}
          >
            {uniqueDates.map((dt, idx) => (
              <Tab
                key={dt}
                label={dt}
                value={idx}
                icon={<ChevronRightIcon />}
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Grid>
        <Grid md={10} sx={{ p: 3 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <ByzMuiTypography isTitle sx={{ fontSize: "24px" }}>
              {t("diet-requests-doctor.date")}
            </ByzMuiTypography>
            <ByzMuiCloseButton onClick={onClose} />
          </Stack>
          <Box sx={{ mt: 2 }}>
            <ByzMuiDataGrid
              rows={rows}
              columns={columns}
              getRowId={(r) => r.id}
              getRowClassName={(params) => (params.id === -1 ? "info-row" : "")}
              pageSize={5}
              customToolbar={ByzDataGridToolbar}
              sx={{
                "& .info-row": {
                  fontWeight: 700,
                  fontSize: "16px",
                },
                "& .status-sent": { backgroundColor: "#AEADA64D" },
                "& .status-approved": { backgroundColor: "#34C7594D" },
                "& .status-rejected": { backgroundColor: "#C734364D" },
                "& .status-pending": { backgroundColor: "#FFCC004D" },
              }}
            />
          </Box>
        </Grid>
      </Grid>
    </Dialog>
  );
}
